import * as PropTypes from 'prop-types';
import * as React from 'react';
import DirectCheckoutWrapper from 'components/shop/direct_checkout_wrapper';
import DirectCheckoutProfileSetup from 'components/shop/direct_checkout_profile_setup';
import DirectCheckoutOnlyPaymentMethod from './direct_checkout_only_payment_method';
import PaypalRadioButton from 'components/shop/paypal_radio_button';
import PaypalProfile from 'components/shop/paypal_profile';
import ErrorMessage from 'components/shared/error_message';

const PAYPAL = 'paypal';
const DIRECT_CHECKOUT = 'direct_checkout';

// This is shown to sellers after they create a listing, before they have set up
// an accepted payment methods profile (Listings::ShopPoliciesController#new).
// It uses some of the same child components as the payment method selection
// component used on the shop policies dashboard.
class ShopPaymentMethodSelection extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedPaymentMethod: props.initiallySelectedPaymentMethod,
      directCheckoutProfileAttributes: props.directCheckoutProfileProps.profileAttributes,
    };
  }

  get directCheckoutSelected() {
    return this.state.selectedPaymentMethod === DIRECT_CHECKOUT;
  }

  get paypalSelected() {
    return this.state.selectedPaymentMethod === PAYPAL;
  }

  nameForField(fieldName) {
    return `${this.props.formPrefix}[${fieldName}]`;
  }

  directCheckoutRadio() {
    if (!this.props.allowDirectCheckout) {
      return false;
    }

    return (
      <DirectCheckoutWrapper
        directCheckoutFeePercent={this.props.directCheckoutFeePercent}
        directCheckoutIntlFeePercent={this.props.directCheckoutIntlFeePercent}
        shopCurrency={this.props.shopCurrency}
        bankCountryCode={this.state.directCheckoutProfileAttributes.countryCode}
        paymentSelection={this.props.paymentSelection}
      >
        <input
          type="radio"
          value="direct_checkout"
          checked={this.directCheckoutSelected}
          name={this.nameForField('payment_method')}
          id="shop-settings-dc-radio"
          onChange={event => this.handleChangePaymentMethod(event)}
        />
      </DirectCheckoutWrapper>
    );
  }

  handleChangePaymentMethod(event) {
    this.setState({
      selectedPaymentMethod: event.target.value,
    });
  }

  updateDirectCheckoutProfileCountryCode(countryCode) {
    const directCheckoutProfileAttributes = Object.assign(
      {},
      this.state.directCheckoutProfileAttributes,
      { countryCode },
    );

    this.setState({ directCheckoutProfileAttributes });
  }

  updateDirectCheckoutProfileCurrency(currency) {
    const directCheckoutProfileAttributes = Object.assign(
      {},
      this.state.directCheckoutProfileAttributes,
      { currency },
    );

    this.setState({ directCheckoutProfileAttributes });
  }

  directCheckoutProfileProps() {
    return Object.assign(
      {},
      this.props.directCheckoutProfileProps,
      {
        profileAttributes: this.state.directCheckoutProfileAttributes,
        selected: this.directCheckoutSelected,
        shopCurrency: this.props.shopCurrency,
        onCountryChange: countryCode => this.updateDirectCheckoutProfileCountryCode(countryCode),
        onCurrencyChange: currency => this.updateDirectCheckoutProfileCurrency(currency),
      },
    );
  }

  directCheckoutProfile() {
    if (!this.props.allowDirectCheckout) {
      return false;
    }

    return (
      <DirectCheckoutProfileSetup {...this.directCheckoutProfileProps()} />
    );
  }

  shopPolicyFormErrors() {
    if (this.props.shopPolicyFormErrors.length === 0) {
      return false;
    }

    return (
      <ErrorMessage
        message={this.props.shopPolicyFormErrors.join('. ')}
        alertStyle="alert-error"
      />
    );
  }

  paymentMethodRadios() {
    return (
      <div>
        {this.directCheckoutRadio()}
        <PaypalRadioButton
          inputName={this.nameForField('payment_method')}
          checked={this.paypalSelected}
          handleChangePaymentMethod={event => this.handleChangePaymentMethod(event)}
          shopCurrency={this.props.shopCurrency}
        />
      </div>
    )
  }

  directCheckoutOnly() {
    return (
      <DirectCheckoutOnlyPaymentMethod
        directCheckoutFeePercent={this.props.directCheckoutFeePercent}
        directCheckoutIntlFeePercent={this.props.directCheckoutIntlFeePercent}
        shopCurrency={this.props.shopCurrency}
        fieldName={this.nameForField('payment_method')}
      />
    )
  }

  allowDCOnly() {
    return this.props.paymentSelection === 'DC_ONLY';
  }

  paymentMethodFields() {
    if (this.allowDCOnly()) {
      return this.directCheckoutOnly();
    }
    return this.paymentMethodRadios();
  }

  sectionHeader() {
    if (this.allowDCOnly()) {
      return I18n.t('js.dashboard.selling.shopPolicies.yourPaymentSettings')
    }
    return I18n.t('js.dashboard.selling.shopPolicies.howCanBuyersPayYou')
  }

  render() {
    return (
      <div>
        <div className="module-header">
          <h3 className="heading-3">{this.sectionHeader()}</h3>
        </div>

        <div className="module pb-0">
          {this.shopPolicyFormErrors()}
          <div className="g-container">
            {this.paymentMethodFields()}
          </div>
        </div>
        <div className="module pt-0 shop-settings-payment-setup">
          {this.directCheckoutProfile()}
        </div>

        <PaypalProfile
          classNames="mb-1 module bd-rounded"
          selected={this.paypalSelected}
          {...this.props.paypalProfileProps}
        />
      </div>
    );
  }
}

ShopPaymentMethodSelection.propTypes = {
  allowDirectCheckout: PropTypes.bool.isRequired,
  directCheckoutFeePercent: PropTypes.number.isRequired,
  directCheckoutIntlFeePercent: PropTypes.number.isRequired,
  directCheckoutProfileProps: PropTypes.shape({
    profileAttributes: PropTypes.object.isRequired,
    payoutCurrencies: PropTypes.object.isRequired,
    bankLookupUrl: PropTypes.string.isRequired,
    countryCodeOptions: PropTypes.array.isRequired,
    formPrefix: PropTypes.string.isRequired,
    selected: PropTypes.bool,
    shopCurrency: PropTypes.string,
  }),
  paypalProfileProps: PropTypes.object.isRequired,
  shopPolicyFormErrors: PropTypes.array.isRequired,
  currencyErrors: PropTypes.array,
  formPrefix: PropTypes.string.isRequired,
  initiallySelectedPaymentMethod: PropTypes.string,
  shopCurrency: PropTypes.string.isRequired,
  paymentSelection: PropTypes.string.isRequired,
};

export default ShopPaymentMethodSelection;



// WEBPACK FOOTER //
// ./javascripts/components/shop/payment_method_selector.jsx